import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
} from "lucide-react";
import axios from "axios";
import { getToken, base_url } from "../../config/config";

// Dashboard layout component
const ProfitLossDashboard3 = () => {
  const [projects, setProjects] = useState([]);
  const [timeFrame, setTimeFrame] = useState("month");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState({
    totalCost: 0,
    totalRevenue: 0,
    totalProfit: 0,
    profitMargin: 0,
    incompleteProjects: 0,
  });
  const [activeProject, setActiveProject] = useState(null);
  const [chartData, setChartData] = useState([]);

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

  useEffect(() => {
    fetchProjects();
  }, [timeFrame]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${base_url}/v1/profit-loss?timeFrame=${timeFrame}`,
      );
      setProjects(response.data.projects);
      processData(response.data.projects);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to load project data. Please try again.");
      setLoading(false);
    }
  };

  const processData = (projectData) => {
    // Calculate summary metrics
    let totalCost = 0;
    let totalRevenue = 0;
    let incompleteProjects = 0;
    let timeFrameData = {};

    projectData.forEach((project) => {
      // Calculate total cost for each project
      const materialCost = parseFloat(project.totalMaterialCost || 0);
      const subcontractorCost = parseFloat(project.totalsubcontratorCost || 0);

      // Calculate labor cost
      let laborCost = 0;
      if (project.projectUsers && project.projectUsers.length > 0) {
        project.projectUsers.forEach((user) => {
          if (user.StartTime && user.EndTime) {
            const startTime = new Date(user.StartTime);
            const endTime = new Date(user.EndTime);
            const hoursDiff = (endTime - startTime) / (1000 * 60 * 60);
            laborCost += hoursDiff * parseFloat(project.MengeCost || 0);
          }
        });
      }

      const totalProjectCost = materialCost + subcontractorCost + laborCost;
      const projectRevenue = parseFloat(project.SaleCost || 0);
      const projectProfit = projectRevenue - totalProjectCost;

      totalCost += totalProjectCost;
      totalRevenue += projectRevenue;

      // Count incomplete projects (missing required fields)
      if (!project.totalMaterialCost || !project.SaleCost) {
        incompleteProjects++;
      }

      // Organize data by timeframe for charts
      const date = project.createdAt ? new Date(project.createdAt) : new Date();
      let timeKey;

      if (timeFrame === "day") {
        timeKey = date.toISOString().split("T")[0];
      } else if (timeFrame === "week") {
        const weekNumber = getWeekNumber(date);
        timeKey = `Week ${weekNumber}`;
      } else if (timeFrame === "month") {
        timeKey = `${date.getFullYear()}-${date.getMonth() + 1}`;
      } else if (timeFrame === "year") {
        timeKey = date.getFullYear().toString();
      }

      if (!timeFrameData[timeKey]) {
        timeFrameData[timeKey] = {
          name: timeKey,
          cost: 0,
          revenue: 0,
          profit: 0,
          projectCount: 0,
        };
      }

      timeFrameData[timeKey].cost += totalProjectCost;
      timeFrameData[timeKey].revenue += projectRevenue;
      timeFrameData[timeKey].profit += projectProfit;
      timeFrameData[timeKey].projectCount += 1;
    });

    // Calculate total profit and profit margin
    const totalProfit = totalRevenue - totalCost;
    const profitMargin =
      totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    setSummaryData({
      totalCost,
      totalRevenue,
      totalProfit,
      profitMargin,
      incompleteProjects,
    });

    // Convert timeframe data object to array for charts
    const chartDataArray = Object.values(timeFrameData);
    setChartData(chartDataArray.sort((a, b) => a.name.localeCompare(b.name)));
  };

  // Helper function to get week number
  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const handleProjectClick = (project) => {
    setActiveProject(project);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(value);
  };

  if (loading)
    return (
      <div className='flex items-center justify-center h-64'>Loading...</div>
    );
  if (error) return <div className='text-red-500 p-4'>{error}</div>;

  return (
    <div className='bg-gray-50 min-h-screen'>
      <div className='container mx-auto px-4 py-6'>
        <h1 className='text-2xl font-bold mb-6'>Profit/Loss Cockpit</h1>

        {/* Time Frame Selector */}
        <div className='mb-6 bg-white p-4 rounded-lg shadow'>
          <div className='flex items-center space-x-2 mb-2'>
            <Calendar size={20} />
            <h2 className='text-lg font-semibold'>Time Frame</h2>
          </div>
          <div className='flex space-x-2'>
            {["day", "week", "month", "year"].map((period) => (
              <button
                key={period}
                onClick={() => setTimeFrame(period)}
                className={`px-4 py-2 rounded-md ${
                  timeFrame === period
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}>
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
          <SummaryCard
            title='Total Revenue'
            value={formatCurrency(summaryData.totalRevenue)}
            icon={<ArrowUpRight className='text-green-500' size={24} />}
            trend={"+4.5%"}
            trendUp={true}
          />
          <SummaryCard
            title='Total Cost'
            value={formatCurrency(summaryData.totalCost)}
            icon={<ArrowDownRight className='text-red-500' size={24} />}
            trend={"+2.1%"}
            trendUp={false}
          />
          <SummaryCard
            title='Total Profit'
            value={formatCurrency(summaryData.totalProfit)}
            icon={
              summaryData.totalProfit >= 0 ? (
                <ArrowUpRight className='text-green-500' size={24} />
              ) : (
                <ArrowDownRight className='text-red-500' size={24} />
              )
            }
            trend={summaryData.totalProfit >= 0 ? "+6.8%" : "-3.2%"}
            trendUp={summaryData.totalProfit >= 0}
          />
          <SummaryCard
            title='Profit Margin'
            value={`${summaryData.profitMargin.toFixed(2)}%`}
            icon={
              summaryData.profitMargin >= 15 ? (
                <ArrowUpRight className='text-green-500' size={24} />
              ) : (
                <AlertTriangle className='text-yellow-500' size={24} />
              )
            }
            trend={
              summaryData.profitMargin >= 15 ? "Healthy" : "Attention Needed"
            }
            trendUp={summaryData.profitMargin >= 15}
          />
        </div>

        {/* Incomplete Projects Warning */}
        {summaryData.incompleteProjects > 0 && (
          <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-r-md'>
            <div className='flex items-center'>
              <AlertTriangle className='text-yellow-500 mr-2' size={20} />
              <p className='text-yellow-700'>
                <span className='font-bold'>
                  {summaryData.incompleteProjects} projects
                </span>{" "}
                have missing cost or revenue data. This may affect the accuracy
                of the financial analysis.
              </p>
            </div>
          </div>
        )}

        {/* Charts Section */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6'>
          {/* Revenue vs Cost Chart */}
          <div className='bg-white p-4 rounded-lg shadow'>
            <h2 className='text-lg font-semibold mb-4'>
              Revenue vs Cost Over Time
            </h2>
            <ResponsiveContainer width='100%' height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='name' />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey='revenue' name='Revenue' fill='#10B981' />
                <Bar dataKey='cost' name='Cost' fill='#F87171' />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Profit Trend Chart */}
          <div className='bg-white p-4 rounded-lg shadow'>
            <h2 className='text-lg font-semibold mb-4'>Profit Trend</h2>
            <ResponsiveContainer width='100%' height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='name' />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line
                  type='monotone'
                  dataKey='profit'
                  name='Profit'
                  stroke='#8884d8'
                  activeDot={{ r: 8 }}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Project List and Details Section */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* Projects List */}
          <div className='bg-white rounded-lg shadow lg:col-span-2'>
            <h2 className='text-lg font-semibold p-4 border-b'>Projects</h2>
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Project
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      ANR
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Cost
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Revenue
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Profit
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {projects.slice(0, 10).map((project) => {
                    // Calculate project finances
                    const materialCost = parseFloat(
                      project.totalMaterialCost || 0,
                    );
                    const subcontractorCost = parseFloat(
                      project.totalsubcontratorCost || 0,
                    );

                    // Calculate labor cost
                    let laborCost = 0;
                    if (
                      project.projectUsers &&
                      project.projectUsers.length > 0
                    ) {
                      project.projectUsers.forEach((user) => {
                        if (user.StartTime && user.EndTime) {
                          const startTime = new Date(user.StartTime);
                          const endTime = new Date(user.EndTime);
                          const hoursDiff =
                            (endTime - startTime) / (1000 * 60 * 60);
                          laborCost +=
                            hoursDiff * parseFloat(project.MengeCost || 0);
                        }
                      });
                    }

                    const totalCost =
                      materialCost + subcontractorCost + laborCost;
                    const revenue = parseFloat(project.SaleCost || 0);
                    const profit = revenue - totalCost;
                    const isMissingData =
                      !project.totalMaterialCost || !project.SaleCost;

                    return (
                      <tr
                        key={project._id}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          activeProject?._id === project._id ? "bg-blue-50" : ""
                        }`}
                        onClick={() => handleProjectClick(project)}>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='text-sm font-medium text-gray-900'>
                            {project.Auftragsbezeichnung || "Unnamed Project"}
                          </div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                          {project.ANR || "N/A"}
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm ${
                            isMissingData
                              ? "text-red-500 font-semibold"
                              : "text-gray-500"
                          }`}>
                          {formatCurrency(totalCost)}
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm ${
                            isMissingData
                              ? "text-red-500 font-semibold"
                              : "text-gray-500"
                          }`}>
                          {formatCurrency(revenue)}
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                            profit >= 0 ? "text-green-600" : "text-red-600"
                          }`}>
                          {formatCurrency(profit)}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              project.status === "fertig"
                                ? "bg-green-100 text-green-800"
                                : project.status === "anghalten"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}>
                            {project.status || "ausstehend"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {projects.length > 10 && (
              <div className='px-6 py-3 text-right text-sm font-medium'>
                <a href='#' className='text-blue-600 hover:text-blue-900'>
                  View all projects
                </a>
              </div>
            )}
          </div>

          {/* Project Details Section */}
          <div className='bg-white rounded-lg shadow'>
            <h2 className='text-lg font-semibold p-4 border-b'>
              {activeProject ? "Project Details" : "Select a Project"}
            </h2>

            {activeProject ? (
              <div className='p-4'>
                <h3 className='text-md font-medium mb-2'>
                  {activeProject.Auftragsbezeichnung || "Unnamed Project"}
                </h3>
                <p className='text-sm text-gray-500 mb-4'>
                  ANR: {activeProject.ANR || "N/A"}
                </p>

                <div className='grid grid-cols-2 gap-4 mb-4'>
                  <div>
                    <p className='text-xs text-gray-500'>Material Cost</p>
                    <p className='text-sm font-medium'>
                      {formatCurrency(
                        parseFloat(activeProject.totalMaterialCost || 0),
                      )}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs text-gray-500'>Subcontractor Cost</p>
                    <p className='text-sm font-medium'>
                      {formatCurrency(
                        parseFloat(activeProject.totalsubcontratorCost || 0),
                      )}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs text-gray-500'>Labor Cost</p>
                    <p className='text-sm font-medium'>
                      {formatCurrency(calculateLaborCost(activeProject))}
                    </p>
                  </div>
                  <div>
                    <p className='text-xs text-gray-500'>Revenue</p>
                    <p className='text-sm font-medium'>
                      {formatCurrency(parseFloat(activeProject.SaleCost || 0))}
                    </p>
                  </div>
                </div>

                {/* Cost Breakdown Chart */}
                <div className='mb-4'>
                  <h4 className='text-sm font-medium mb-2'>Cost Breakdown</h4>
                  <ResponsiveContainer width='100%' height={200}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Material",
                            value: parseFloat(
                              activeProject.totalMaterialCost || 0,
                            ),
                          },
                          {
                            name: "Subcontractors",
                            value: parseFloat(
                              activeProject.totalsubcontratorCost || 0,
                            ),
                          },
                          {
                            name: "Labor",
                            value: calculateLaborCost(activeProject),
                          },
                        ]}
                        cx='50%'
                        cy='50%'
                        labelLine={false}
                        outerRadius={80}
                        fill='#8884d8'
                        dataKey='value'
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }>
                        {[
                          {
                            name: "Material",
                            value: parseFloat(
                              activeProject.totalMaterialCost || 0,
                            ),
                          },
                          {
                            name: "Subcontractors",
                            value: parseFloat(
                              activeProject.totalsubcontratorCost || 0,
                            ),
                          },
                          {
                            name: "Labor",
                            value: calculateLaborCost(activeProject),
                          },
                        ].map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Missing Fields Section */}
                {(!activeProject.totalMaterialCost ||
                  !activeProject.SaleCost ||
                  !activeProject.MengeCost) && (
                  <div className='bg-red-50 p-3 rounded-md mb-4'>
                    <h4 className='text-sm font-medium text-red-800 mb-1'>
                      Missing Fields
                    </h4>
                    <ul className='text-xs text-red-700 list-disc pl-4'>
                      {!activeProject.totalMaterialCost && (
                        <li>Material Cost</li>
                      )}
                      {!activeProject.SaleCost && <li>Sale Cost</li>}
                      {!activeProject.MengeCost && <li>Menge Cost</li>}
                    </ul>
                  </div>
                )}

                {/* Edit Button */}
                <button
                  className='w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors'
                  onClick={() =>
                    (window.location.href = `/projects/edit/${activeProject._id}`)
                  }>
                  Edit Project
                </button>
              </div>
            ) : (
              <div className='p-6 text-center text-gray-500'>
                Select a project from the list to view details
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate labor cost
const calculateLaborCost = (project) => {
  let laborCost = 0;
  if (project.projectUsers && project.projectUsers.length > 0) {
    project.projectUsers.forEach((user) => {
      if (user.StartTime && user.EndTime) {
        const startTime = new Date(user.StartTime);
        const endTime = new Date(user.EndTime);
        const hoursDiff = (endTime - startTime) / (1000 * 60 * 60);
        laborCost += hoursDiff * parseFloat(project.MengeCost || 0);
      }
    });
  }
  return laborCost;
};

// Summary Card Component
const SummaryCard = ({ title, value, icon, trend, trendUp }) => {
  return (
    <div className='bg-white p-4 rounded-lg shadow'>
      <div className='flex justify-between items-start mb-2'>
        <h3 className='text-gray-500 text-sm'>{title}</h3>
        {icon}
      </div>
      <p className='text-2xl font-bold mb-1'>{value}</p>
      <p className={`text-xs ${trendUp ? "text-green-500" : "text-red-500"}`}>
        {trend}
      </p>
    </div>
  );
};

export default ProfitLossDashboard3;
